/**
 * Pipeline Page Component
 *
 * Full page view for monitoring a note's processing pipeline.
 * Route: /pipeline/:id
 */

import { Link, useParams } from "react-router";
import { ArrowLeft, RefreshCw } from "lucide-react";
import { PipelineMonitor } from "../../components/pipeline";
import { RecommendationPanel } from "../../components/recommendation/RecommendationPanel";
import { useNotePipeline } from "../../hooks/useNotePipeline";
import { useRecommendations } from "../../hooks/useRecommendations";

export function PipelinePage() {
  const { id: noteId } = useParams<{ id: string }>();

  const {
    data: note,
    pipelineState,
    isLoading,
    isError,
    error,
    refetch,
    isProcessing,
    isReviewing,
  } = useNotePipeline(noteId);

  // Recommendations — enabled once note reaches COMPLETED status
  const completedId = note?.status === "COMPLETED" ? noteId : undefined;
  const {
    recommendations,
    isLoading: recsLoading,
    rate: rateRec,
  } = useRecommendations(completedId);

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-[calc(100vh-3rem)] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-10 h-10 border-4 border-accent-blue border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-text-muted text-sm">Loading pipeline status...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (isError) {
    return (
      <div className="min-h-[calc(100vh-3rem)] flex items-center justify-center">
        <div className="card p-8 max-w-md text-center">
          <div className="w-14 h-14 bg-accent-red/15 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl text-accent-red">!</span>
          </div>
          <h2 className="text-lg font-semibold text-text-primary mb-2">
            Error Loading Note
          </h2>
          <p className="text-text-muted text-sm mb-4">
            {error instanceof Error ? error.message : "An error occurred"}
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-accent-blue hover:text-accent-blue/80 text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  // No pipeline state
  if (!pipelineState || !note) {
    return (
      <div className="min-h-[calc(100vh-3rem)] flex items-center justify-center">
        <div className="text-center">
          <p className="text-text-muted text-sm">Note not found</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-accent-blue hover:text-accent-blue/80 text-sm mt-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-3rem)]">
      {/* Header */}
      <header className="bg-bg-secondary border-b border-border">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-text-muted hover:text-text-primary transition-colors text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Dashboard</span>
            </Link>

            <div className="flex items-center gap-3">
              {isProcessing && (
                <span className="inline-flex items-center gap-2 px-2.5 py-1 bg-accent-yellow/15 text-accent-yellow rounded-sm text-xs font-medium">
                  <span className="w-1.5 h-1.5 bg-accent-yellow rounded-full animate-pulse" />
                  Processing...
                </span>
              )}
              {isReviewing && (
                <span className="inline-flex items-center gap-2 px-2.5 py-1 bg-accent-yellow/15 text-accent-yellow rounded-sm text-xs font-medium">
                  <span className="w-1.5 h-1.5 bg-accent-yellow rounded-full animate-pulse" />
                  Awaiting Review
                </span>
              )}

              <button
                type="button"
                onClick={() => refetch()}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-bg-hover text-text-muted rounded-sm hover:bg-border hover:text-text-primary transition-colors text-xs"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Refresh
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-6xl mx-auto px-4 py-6">
        {/* Page title */}
        <div className="mb-4">
          <h1 className="text-xl font-bold text-text-primary">
            Pipeline Monitor
          </h1>
          <p className="text-text-muted text-sm mt-1">
            Viewing processing pipeline for note{" "}
            <code className="bg-bg-hover px-1.5 py-0.5 rounded-sm font-mono text-xs text-accent-cyan">
              {noteId?.slice(0, 8)}...
            </code>
          </p>
        </div>

        {/* Pipeline Monitor Component */}
        <PipelineMonitor
          pipelineState={pipelineState}
          noteStatus={note.status}
          note={note}
          onReviewComplete={() => refetch()}
        />

        {/* Note content preview */}
        {note.content_text && (
          <div className="mt-4 card p-4">
            <h3 className="text-xs font-semibold text-text-primary mb-2">
              Note Content
            </h3>
            <p className="text-text-muted text-xs whitespace-pre-wrap">
              {note.content_text.slice(0, 500)}
              {note.content_text.length > 500 && "..."}
            </p>
          </div>
        )}

        {/* Summary */}
        {note.summary_text && (
          <div className="mt-3 bg-accent-green/10 border border-accent-green/20 rounded-lg p-4">
            <h3 className="text-xs font-semibold text-accent-green mb-2">
              Generated Summary
            </h3>
            <p className="text-accent-green/80 text-xs whitespace-pre-wrap">
              {note.summary_text}
            </p>
          </div>
        )}

        {/* Recommendations — shown when note processing is complete */}
        {completedId && (
          <div className="mt-4">
            <RecommendationPanel
              noteId={completedId}
              recommendations={recommendations}
              isLoading={recsLoading}
              onRate={rateRec}
            />
          </div>
        )}
      </main>
    </div>
  );
}
